import { capitalize, Dictionary, find, flatten, get } from 'lodash';
import { parseMixin, toExpression } from '..';
import { MixinOperandType, MixinSyntaxTree } from '../types';
import { MixinTemplate } from './types';

export interface ApplyableTemplate {
  arguments: string[];
  expression: string;
  definition: string;
  title?: string;
}

/**
 * Find the template that should be used to parse a given template expression
 * @param expression a template expression
 * @param templates a set of candidate templates to check
 * @returns a template matching the given mixin expression
 */
export function findTemplateForExpression(expression: string, templates: MixinTemplate[]): MixinTemplate {
  const found = find(templates, template => templateMatchesExpression(template, expression));
  if (!found) {
    throw new Error(`No template found matching expression ${expression}`);
  }
  return found;
}

/**
 * Use a given template to parse a template expression into a mixin expression
 * @param expression a template expression
 * @param template a template that can be used to parse the given expression
 * @returns a parsed mixin expression
 * @throws an error if the template does not match the expression (i.e., the template registers arguments that are not
 *  present in the template expression)
 */
export function applyExpressionToTemplate(expression: string, template: ApplyableTemplate): string {
  const groups = getTemplateGroups(expression, template);
  return template.arguments.reduce((mixinExpression, parameter) => {
    const productCode = groups[parameter];
    if (!productCode) {
      throw new Error(
        `Failed to match parameter ${parameter} of template ${template.expression} to expression ${expression}`,
      );
    }
    return mixinExpression.replace(parameter, productCode);
  }, template.definition);
}

export function templateMatchesExpression(template: ApplyableTemplate, expression: string): boolean {
  const templateMatcher = templateToRegex(template);
  return templateMatcher.test(expression);
}

function templateToRegex(template: { arguments: string[]; expression: string }, parameter: string | null = null) {
  const args = parameter ? [parameter] : template.arguments;
  const body = args.reduce((acc, argument) => acc.replace(argument, `(?<${argument}>[A-Z]{5})`), template.expression);
  return new RegExp(`^${body}$`);
}

function getTemplateGroups(expression: string, template: { arguments: string[]; expression: string }) {
  const templateMatcher = templateToRegex(template);
  const groups = templateMatcher.exec(expression)?.groups;
  if (!groups) {
    throw new Error(`Failed to apply expression ${expression} to template ${template.expression}`);
  }
  return groups;
}

export function extractArgumentsFromTemplateExpression(templateExpression: string) {
  return Array.from(templateExpression.matchAll(/[XYZ]/g), m => m[0]);
}

export function applyExpressionToTitle(
  expression: string,
  template: { arguments: string[]; expression: string; title?: string },
  products: Dictionary<{ title: string }>,
): string | null {
  const title = template.title;
  if (!title) {
    return null;
  }
  const groups = getTemplateGroups(expression, template);
  return Object.keys(groups).reduce((t, key) => {
    const productCode = groups[key];
    const value = get(products, productCode, undefined);
    if (!value) {
      throw new Error(`Could not find product ${productCode}`);
    }
    return t.replaceAll(key, value.title.toLowerCase());
  }, title);
}

export function assertArgument(template: { arguments: string[]; expression: string }, maybeArguments: string[]) {
  const templateArguments = template.arguments;
  if (templateArguments.length !== maybeArguments.length || !templateArguments.every(a => maybeArguments.includes(a))) {
    throw new Error(`Invalid arguments for template: ${template.expression}`);
  }
}

export function applyArgumentsToTitle(
  template: { arguments: string[]; expression: string; title?: string },
  products: Dictionary<{ title: string }>,
  productsByArgument: Dictionary<string>,
) {
  assertArgument(template, Object.keys(productsByArgument));
  const title = template.title;
  if (!title) {
    return null;
  }
  const concatenated = Object.keys(productsByArgument).reduce((t, key) => {
    const productCode = productsByArgument[key];
    const value = get(products, productCode, undefined);
    if (!value) {
      throw new Error(`Could not find product ${productCode}`);
    }
    return t.replaceAll(key, value.title.toLowerCase());
  }, title);
  return capitalize(concatenated);
}

export function applyArgumentsToTemplate(
  template: { arguments: string[]; expression: string; definition: string },
  productsByArgument: Dictionary<string>,
): string {
  assertArgument(template, Object.keys(productsByArgument));
  const tree = parseMixin(template.definition);
  const filledTree = fillArguments(tree, productsByArgument);
  const filledExpression = toExpression(filledTree);
  try {
    parseMixin(filledExpression);
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'unknown reason';
    throw new Error(`Failed to parse mixin expression ${filledExpression}: ${reason}`);
  }
  return filledExpression;
}

function fillArguments(tree: MixinSyntaxTree, productsByArgument: Dictionary<string>): MixinSyntaxTree {
  const productOrThrow = (arg: string): string => {
    const productCode = get(productsByArgument, arg, undefined);
    if (!productCode) {
      throw new Error(`Missing argument for: '${arg}'`);
    }
    return productCode;
  };
  switch (tree.type) {
    case MixinOperandType.TEMPLATE_ARGUMENT:
      return {
        type: MixinOperandType.PRODUCT,
        arguments: [productOrThrow(tree.arguments[0])],
      };
    case MixinOperandType.ATTRIBUTE:
    case MixinOperandType.NUMBER:
    case MixinOperandType.PRODUCT:
      return tree;
    default:
      return {
        type: tree.type,
        arguments: tree.arguments.map(a => fillArguments(a, productsByArgument)),
      };
  }
}

export function extractArguments(expression: string) {
  const tree = parseMixin(expression);
  return getArguments(tree);
}

export function getArguments(tree: MixinSyntaxTree): string[] {
  switch (tree.type) {
    case MixinOperandType.TEMPLATE_ARGUMENT:
      return [tree.arguments[0]];
    case MixinOperandType.ATTRIBUTE:
    case MixinOperandType.NUMBER:
    case MixinOperandType.PRODUCT:
      return [];
    default:
      return flatten(tree.arguments.map(operand => getArguments(operand as MixinSyntaxTree)));
  }
}
